/**
 * msi-corp_debug-logger.js
 * ---------------------------------------------------------------------------
 * 輕量診斷工具（通用，不綁定任何特定 App）。提供三件事：
 *
 *   1. 分級輸出 (phase/debug/info/warn/error) ── debug 開啟時才印到 console。
 *   2. 環形緩衝區 (records) ── 即使 debug 關閉也持續記錄最近 N 筆，
 *                              線上出事時可請使用者執行 dump() 取回完整軌跡。
 *   3. 失控迴圈斷路器 (watchRunaway) ── 偵測「單位時間內被呼叫過多次」，
 *                              觸發時呼叫 onTrip 讓呼叫端安全停機。
 *
 * 斷路器與 error() 的輸出「不受 debug 開關影響」——那是安全機制，不是除錯訊息。
 *
 * 【GDPR】不寫入 cookie / localStorage / sessionStorage，不傳送任何資料到外部。
 *         debug 只能由網址參數或 HTML 屬性開啟，因此不留下任何裝置端痕跡。
 * ---------------------------------------------------------------------------
 */

/** 各層級對應的 console 方法。 */
const CONSOLE_METHOD = { phase: 'log', debug: 'debug', info: 'info', warn: 'warn', error: 'error' };

/**
 * 判斷是否要開啟 debug。
 * 優先序：網址參數（依序取第一個有出現的）> 傳入的預設值。
 * 刻意不支援 storage 以免留下裝置端痕跡。
 * @param {string|string[]} paramNames 網址參數名稱，例如 `msi_debug` 或 `['msi_debug', 'msi_ako_debug']`
 * @param {boolean} fallback
 */
export function resolveDebugFlag(paramNames, fallback = false) {
  const names = Array.isArray(paramNames) ? paramNames : [paramNames];
  try {
    const params = new URL(window.location.href).searchParams;
    for (const name of names) {
      const value = params.get(name);
      if (value === null) continue;
      return value !== '0' && value !== 'false';
    }
    return fallback;
  } catch {
    return fallback;
  }
}

/**
 * @param {object} options
 * @param {string} options.namespace  console 前綴，例如 `MSI/AkoGuard`
 * @param {boolean} [options.enabled] 是否輸出到 console
 * @param {number} [options.bufferSize] 環形緩衝區容量
 */
export function createLogger({ namespace, enabled = false, bufferSize = 300 } = {}) {
  const createdAt = performance.now();
  /** @type {Array<{t:number, level:string, message:string, detail:*}>} */
  const buffer = [];
  const counters = new Map();
  let isEnabled = Boolean(enabled);

  const elapsed = () => Math.round(performance.now() - createdAt);

  /** 寫入環形緩衝區；`force` 為 true 時即使 debug 關閉也輸出到 console。 */
  const record = (level, message, detail, force = false) => {
    const entry = { t: elapsed(), level, message, detail };
    buffer.push(entry);
    if (buffer.length > bufferSize) buffer.shift();

    if (isEnabled || force) {
      const prefix = `[${namespace}] +${entry.t}ms ${message}`;
      console[CONSOLE_METHOD[level]](prefix, ...(detail === undefined ? [] : [detail]));
    }
    return entry;
  };

  return {
    get enabled() {
      return isEnabled;
    },
    set enabled(value) {
      isEnabled = Boolean(value);
    },

    /** 標記流程走到哪個階段，例如 `boot`、`detect:start`。 */
    phase: (name, detail) => record('phase', `▸ ${name}`, detail),

    debug: (message, detail) => record('debug', message, detail),
    info: (message, detail) => record('info', message, detail),
    warn: (message, detail) => record('warn', message, detail),
    /** 錯誤一律輸出，不受 debug 開關影響。 */
    error: (message, detail) => record('error', message, detail, true),

    /** 累加計數器，回傳最新值。 */
    count(name) {
      const next = (counters.get(name) || 0) + 1;
      counters.set(name, next);
      return next;
    },

    counters: () => Object.fromEntries(counters),

    /**
     * 失控迴圈斷路器。
     * 呼叫端在每次「可能被自己觸發」的操作前呼叫 tick()；
     * 回傳 false 代表已跳閘，呼叫端必須立刻停止該操作。
     *
     * @param {string} name
     * @param {object} options
     * @param {number} [options.limit]    視窗內允許的最大次數
     * @param {number} [options.windowMs] 滑動視窗長度
     * @param {() => void} [options.onTrip] 跳閘時的安全停機動作
     */
    watchRunaway(name, { limit = 150, windowMs = 1000, onTrip } = {}) {
      let windowStart = performance.now();
      let count = 0;
      let tripped = false;
      let peak = 0;

      return {
        get tripped() {
          return tripped;
        },
        get peak() {
          return peak;
        },
        tick() {
          if (tripped) return false;

          const now = performance.now();
          if (now - windowStart > windowMs) {
            windowStart = now;
            count = 0;
          }
          count += 1;
          if (count > peak) peak = count;

          if (count <= limit) return true;

          tripped = true;
          record(
            'error',
            `失控迴圈保護已跳閘：${name}（${windowMs}ms 內被呼叫 ${count} 次，上限 ${limit}）`,
            { name, count, limit, windowMs },
            true,
          );
          if (onTrip) {
            try {
              onTrip();
            } catch (error) {
              record('error', `斷路器 onTrip 失敗：${name}`, error, true);
            }
          }
          return false;
        },
      };
    },

    /** 取得原始紀錄（供程式判讀）。 */
    records: () => buffer.slice(),

    /** 人工檢視：在 Console 輸入 window.MSI.apps.get('<id>').logger.dump() 即可。 */
    dump() {
      console.groupCollapsed(`[${namespace}] 診斷軌跡（${buffer.length} 筆 / +${elapsed()}ms）`);
      console.table(buffer.map(({ t, level, message, detail }) => ({ 'ms': t, level, message, detail })));
      console.log('counters', Object.fromEntries(counters));
      console.groupEnd();
      return buffer.slice();
    },
  };
}
